import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Snackbar,
  Tooltip,
  Fab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Download as DownloadIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';

interface AppVersion {
  _id: string;
  platform: 'android' | 'ios' | 'all';
  version: string;
  buildNumber: number;
  isUpdateAvailable: boolean;
  isForceUpdate: boolean;
  minSupportedVersion: string;
  downloadUrl: {
    android: string;
    ios: string;
  };
  releaseNotes: string;
  features: string[];
  bugFixes: string[];
  improvements: string[];
  isActive: boolean;
  releaseDate: string;
  createdBy: {
    name: string;
    email: string;
  };
  updatedBy?: {
    name: string;
    email: string;
  };
}

interface VersionFormData {
  platform: 'android' | 'ios' | 'all';
  version: string;
  buildNumber: number;
  minSupportedVersion: string;
  downloadUrl: {
    android: string;
    ios: string;
  };
  releaseNotes: string;
  features: string[];
  bugFixes: string[];
  improvements: string[];
}

const VersionManagement: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingVersion, setEditingVersion] = useState<AppVersion | null>(null);
  const [formData, setFormData] = useState<VersionFormData>({
    platform: 'all',
    version: '',
    buildNumber: 1,
    minSupportedVersion: '1.0.0',
    downloadUrl: {
      android: '',
      ios: ''
    },
    releaseNotes: '',
    features: [],
    bugFixes: [],
    improvements: []
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  const queryClient = useQueryClient();

  // Fetch versions
  const { data: versionsData, isLoading, error } = useQuery({
    queryKey: ['versions'],
    queryFn: () => apiService.getAllVersions()
  });

  // Create version mutation
  const createVersionMutation = useMutation({
    mutationFn: (data: VersionFormData) => apiService.createVersion(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions'] });
      setSnackbar({
        open: true,
        message: 'Version created successfully',
        severity: 'success'
      });
      handleCloseDialog();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to create version',
        severity: 'error'
      });
    }
  });

  // Update version mutation
  const updateVersionMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<VersionFormData> }) =>
      apiService.updateVersion(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions'] });
      setSnackbar({
        open: true,
        message: 'Version updated successfully',
        severity: 'success'
      });
      handleCloseDialog();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update version',
        severity: 'error'
      });
    }
  });

  // Delete version mutation
  const deleteVersionMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteVersion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions'] });
      setSnackbar({
        open: true,
        message: 'Version deleted successfully',
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to delete version',
        severity: 'error'
      });
    }
  });

  // Toggle update availability mutation
  const toggleUpdateMutation = useMutation({
    mutationFn: ({ id, isUpdateAvailable }: { id: string; isUpdateAvailable: boolean }) =>
      apiService.toggleUpdateStatus(id, isUpdateAvailable),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions'] });
      setSnackbar({
        open: true,
        message: 'Update status toggled successfully',
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to toggle update status',
        severity: 'error'
      });
    }
  });

  // Toggle force update mutation
  const toggleForceUpdateMutation = useMutation({
    mutationFn: ({ id, isForceUpdate }: { id: string; isForceUpdate: boolean }) =>
      apiService.toggleForceUpdate(id, isForceUpdate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions'] });
      setSnackbar({
        open: true,
        message: 'Force update status toggled successfully',
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to toggle force update status',
        severity: 'error'
      });
    }
  });

  const handleOpenDialog = (version?: AppVersion) => {
    if (version) {
      setEditingVersion(version);
      setFormData({
        platform: version.platform,
        version: version.version,
        buildNumber: version.buildNumber,
        minSupportedVersion: version.minSupportedVersion,
        downloadUrl: version.downloadUrl,
        releaseNotes: version.releaseNotes,
        features: version.features,
        bugFixes: version.bugFixes,
        improvements: version.improvements
      });
    } else {
      setEditingVersion(null);
      setFormData({
        platform: 'all',
        version: '',
        buildNumber: 1,
        minSupportedVersion: '1.0.0',
        downloadUrl: {
          android: '',
          ios: ''
        },
        releaseNotes: '',
        features: [],
        bugFixes: [],
        improvements: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingVersion(null);
    setFormData({
      platform: 'all',
      version: '',
      buildNumber: 1,
      minSupportedVersion: '1.0.0',
      downloadUrl: {
        android: '',
        ios: ''
      },
      releaseNotes: '',
      features: [],
      bugFixes: [],
      improvements: []
    });
  };

  const handleSubmit = () => {
    if (editingVersion) {
      updateVersionMutation.mutate({
        id: editingVersion._id,
        data: formData
      });
    } else {
      createVersionMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this version?')) {
      deleteVersionMutation.mutate(id);
    }
  };

  const handleToggleUpdate = (id: string, currentStatus: boolean) => {
    toggleUpdateMutation.mutate({
      id,
      isUpdateAvailable: !currentStatus
    });
  };

  const handleToggleForceUpdate = (id: string, currentStatus: boolean) => {
    toggleForceUpdateMutation.mutate({
      id,
      isForceUpdate: !currentStatus
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case 'android': return 'success';
      case 'ios': return 'info';
      case 'all': return 'primary';
      default: return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading versions...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load versions. Please try again.
      </Alert>
    );
  }

  // Extract versions array from the nested response structure
  const versions = versionsData?.data?.data || versionsData?.data || [];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Version Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add New Version
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Platform</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Build</TableCell>
                  <TableCell>Update Available</TableCell>
                  <TableCell>Force Update</TableCell>
                  <TableCell>Release Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(versions) ? versions.map((version: AppVersion) => (
                  <TableRow key={version._id}>
                    <TableCell>
                      <Chip
                        label={version.platform.toUpperCase()}
                        color={getPlatformColor(version.platform) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {version.version}
                      </Typography>
                    </TableCell>
                    <TableCell>{version.buildNumber}</TableCell>
                    <TableCell>
                      <Tooltip title={version.isUpdateAvailable ? 'Disable Update' : 'Enable Update'}>
                        <IconButton
                          onClick={() => handleToggleUpdate(version._id, version.isUpdateAvailable)}
                          color={version.isUpdateAvailable ? 'success' : 'default'}
                        >
                          {version.isUpdateAvailable ? <ToggleOnIcon /> : <ToggleOffIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={version.isForceUpdate ? 'Disable Force Update' : 'Enable Force Update'}>
                        <IconButton
                          onClick={() => handleToggleForceUpdate(version._id, version.isForceUpdate)}
                          color={version.isForceUpdate ? 'error' : 'default'}
                          disabled={!version.isUpdateAvailable}
                        >
                          {version.isForceUpdate ? <ToggleOnIcon /> : <ToggleOffIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{formatDate(version.releaseDate)}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit Version">
                        <IconButton
                          onClick={() => handleOpenDialog(version)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Version">
                        <IconButton
                          onClick={() => handleDelete(version._id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">
                        No versions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingVersion ? 'Edit Version' : 'Add New Version'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={formData.platform}
                  onChange={(e) => setFormData({ ...formData, platform: e.target.value as any })}
                  label="Platform"
                >
                  <MenuItem value="all">All Platforms</MenuItem>
                  <MenuItem value="android">Android</MenuItem>
                  <MenuItem value="ios">iOS</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Version (x.y.z)"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder="1.0.0"
                helperText="Format: x.y.z"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Build Number"
                type="number"
                value={formData.buildNumber}
                onChange={(e) => setFormData({ ...formData, buildNumber: parseInt(e.target.value) || 1 })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Min Supported Version"
                value={formData.minSupportedVersion}
                onChange={(e) => setFormData({ ...formData, minSupportedVersion: e.target.value })}
                placeholder="1.0.0"
                helperText="Minimum version required"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Android Download URL"
                value={formData.downloadUrl.android}
                onChange={(e) => setFormData({
                  ...formData,
                  downloadUrl: { ...formData.downloadUrl, android: e.target.value }
                })}
                placeholder="https://play.google.com/store/apps/details?id=..."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="iOS Download URL"
                value={formData.downloadUrl.ios}
                onChange={(e) => setFormData({
                  ...formData,
                  downloadUrl: { ...formData.downloadUrl, ios: e.target.value }
                })}
                placeholder="https://apps.apple.com/app/..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Release Notes"
                multiline
                rows={4}
                value={formData.releaseNotes}
                onChange={(e) => setFormData({ ...formData, releaseNotes: e.target.value })}
                placeholder="What's new in this version..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createVersionMutation.isPending || updateVersionMutation.isPending}
          >
            {editingVersion ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VersionManagement;
